import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Mail } from "lucide-react";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "contacted", "done"];

export default function Enquiries() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/enquiries").then(({ data }) => setItems(data)).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load enquiries");
      setItems([]);
    });
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}`, { status });
      setItems((list) => list.map((e) => (e.id === id ? data : e)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="enquiries-page">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2">Get Set Design</div>
        <h1 className="text-4xl sm:text-5xl tracking-tighter font-black mb-10">Custom logo enquiries</h1>
        {items === null ? (
          <p className="text-sm text-[#52525B]">Loading…</p>
        ) : items.length === 0 ? (
          <div className="border border-black/10 bg-[#F8F9FA] p-10 text-center" data-testid="enquiries-empty">
            <Inbox className="w-6 h-6 mx-auto text-[#ED5A24] mb-3" />
            <p className="text-sm text-[#52525B]">No enquiries yet. They'll appear here when visitors use the home page banner.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="enquiries-list">
            {items.map((e) => <EnquiryRow key={e.id} e={e} onStatus={setStatus} />)}
          </div>
        )}
      </div>
    </div>
  );
}

const EnquiryRow = ({ e, onStatus }) => (
  <div className="border border-black/10 p-6" data-testid={`enquiry-${e.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
      <div>
        <div className="font-bold text-lg">{e.name}{e.business && <span className="text-[#52525B] font-normal"> · {e.business}</span>}</div>
        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-sm text-[#ED5A24] hover:underline" data-testid={`enquiry-email-${e.id}`}>
          <Mail className="w-3.5 h-3.5" /> {e.email}
        </a>
      </div>
      <div className="flex items-center gap-1 border border-black/15">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onStatus(e.id, s)}
            data-testid={`enquiry-status-${s}-${e.id}`}
            className={`text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5 transition-colors duration-200 ${
              e.status === s ? "bg-[#0A0A0B] text-white" : "text-[#52525B] hover:text-[#0A0A0B]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
    <p className="text-sm whitespace-pre-wrap leading-relaxed">{e.brief}</p>
    <div className="text-xs text-[#52525B] mt-3">{new Date(e.created_at).toLocaleString()}</div>
  </div>
);
